import { activityDateMillis, hasActivityDateValue, NO_ACTIVITY_HISTORY_WARNING } from "./crmRepairConsoleUtils";

export const QUOTE_INDEX_REPAIR_MODES = [
  ["display_only", "Repair Display Fields Only"],
  ["activity_only", "Repair Activity Date Only"],
  ["financial_only", "Repair Financial Snapshot"],
  ["include_customer_fields", "Repair Customer Fields"],
  ["include_status_stage", "Repair Status / Stage"],
  ["include_link_fields", "Repair Link Fields"],
];

const FIELDS = {
  display_only: ["cardTitle", "displayTitle", "searchTitle", "customerDisplayName", "siteName", "quoteNo", "quoteType", "searchText", "searchableText", "searchKeywords", "searchTokens"],
  financial_only: ["subtotal", "gst", "gstAmount", "totalGst", "taxableAmount", "grandTotal", "finalTotal", "totalAmount", "total", "finalAmount", "balanceAmount", "paymentSummary"],
  include_customer_fields: ["customerId", "customerName", "customerPhone", "customerEmail", "email", "customerSnapshot"],
  include_status_stage: ["status", "quoteStage", "workflowStatus"],
  include_link_fields: ["opportunityId", "dealId", "projectId"],
};

export function repairValueText(value) {
  if (value == null) return "Missing";
  const millis = typeof value === "object" ? activityDateMillis(value) : null;
  return millis != null ? new Date(millis).toISOString() : typeof value === "object" ? JSON.stringify(value) : String(value);
}

export function buildQuoteIndexRepairPreview({ fullQuote = {}, current = {}, expected = {}, mode }) {
  let warning = "";
  let source = "saved quotation";
  const proposed = {};
  if (mode === "activity_only") {
    if (hasActivityDateValue(current.lastActivityAt)) warning = "This record already has an activity date. Run a new dry run.";
    const candidates = [
      ...["lastActivityAt", "updatedAt", "createdAt", "quoteDate", "date"].map((key) => [`fullQuote.${key}`, fullQuote[key]]),
      ...["updatedAt", "createdAt"].map((key) => [`quoteIndex.${key}`, current[key]]),
    ];
    const candidate = candidates.find(([, value]) => activityDateMillis(value) != null);
    source = candidate?.[0] || "none";
    proposed.lastActivityAt = candidate?.[1] ?? null;
    if (!candidate) warning = NO_ACTIVITY_HISTORY_WARNING;
  } else {
    if (!FIELDS[mode]) throw new Error("Unknown quote index repair mode.");
    // The display index builder omits canonical link and workflow fields.
    // Copy these modes from the saved source, not its lossy listing projection.
    const values = ["financial_only", "include_link_fields", "include_status_stage"].includes(mode) ? fullQuote : expected;
    for (const field of FIELDS[mode]) {
      if (Object.prototype.hasOwnProperty.call(values, field) && values[field] != null) proposed[field] = values[field];
    }
    if (mode === "financial_only") {
      const savedAliases = {
        grandTotal: ["grandTotal", "finalTotal", "totalAmount", "total", "finalAmount"],
        finalTotal: ["finalTotal", "grandTotal", "finalAmount", "totalAmount", "total"],
        totalAmount: ["totalAmount", "grandTotal", "finalTotal", "total", "finalAmount"],
        total: ["total", "grandTotal", "finalTotal", "totalAmount", "finalAmount"],
        finalAmount: ["finalAmount", "finalTotal", "grandTotal", "totalAmount", "total"],
      };
      for (const [field, aliases] of Object.entries(savedAliases)) {
        if (!Object.prototype.hasOwnProperty.call(current, field) || proposed[field] != null) continue;
        const saved = [fullQuote, fullQuote.totals || {}, fullQuote.summary || {}].flatMap((record) => aliases.map((key) => record[key])).find((value) => value != null && value !== "" && typeof value !== "object" && Number.isFinite(Number(value)));
        if (saved != null) proposed[field] = saved;
      }
      for (const [field, value] of Object.entries(proposed)) {
        if (field !== "paymentSummary" && (typeof value === "object" || value === "" || !Number.isFinite(Number(value)))) {
          delete proposed[field];
          warning = "A saved financial value is invalid. Review manually; totals will not be recalculated.";
        }
      }
    }
    if (proposed.customerSnapshot) {
      proposed.customerSnapshot = Object.fromEntries(["customerId", "customerName", "name", "displayName", "phone", "customerPhone", "email", "customerEmail", "address"].filter((field) => proposed.customerSnapshot[field] != null).map((field) => [field, proposed.customerSnapshot[field]]));
    }
    if (mode === "display_only") {
      // Titles/search use the current identity until a separate customer repair is approved.
      if (String(current.customerName || "") !== String(expected.customerName || "")) {
        for (const field of ["cardTitle", "displayTitle", "searchTitle", "customerDisplayName", "searchText", "searchableText", "searchKeywords", "searchTokens"]) delete proposed[field];
      } else proposed.customerDisplayName = current.customerName || "";
      for (const field of ["referralPartnerId", "referralPartnerName"]) {
        if (fullQuote[field]) proposed[field] = fullQuote[field];
      }
    }
    if (mode === "include_customer_fields" && (!expected.customerId || !expected.customerName)) warning = "A clear saved customerId and customerName are required. Review manually.";
  }
  const fields = Object.entries(proposed).map(([field, value]) => ({ field, before: current[field] ?? null, after: value, source, changed: repairValueText(current[field]) !== repairValueText(value) }));
  const patch = warning ? {} : Object.fromEntries(fields.filter((field) => field.changed).map((field) => [field.field, field.after]));
  return { mode, fields, patch, warning, source, risk: warning ? "Manual Only" : "Needs Review" };
}

export function quoteIndexRepairConfirmation(mode) {
  if (mode === "financial_only") return "You are updating the Saved Quotes financial snapshot from the saved quotation. This will not recalculate the quote.";
  if (mode === "activity_only") return "You are updating the activity date used for sorting. This may affect where the record appears in Saved Quotes, CRM, or Orders.";
  if (mode === "include_customer_fields") return "Update only the customer identity fields shown in this preview?";
  if (mode === "include_status_stage") return "Update only the status/stage fields shown? This affects filtering.";
  if (mode === "include_link_fields") return "Update only the opportunity, deal and project links shown?";
  return "Update only the display/search fields shown? Financial values, dates, status and customer identity will not change.";
}
